import {
  Button,
  CogFilledSVG,
  FlagHollowSVG,
  Toast,
  TooltipDialog,
  useModal,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import { handleHideChecksForUnderstanding } from "@/assignments/utils/assignmentActions";
import globalToasterRef from "@/base/globalToasterRef";
import useHasFeature from "@/features/useHasFeature";
import ReportContentFormModal from "@/view/ReportContentFormModal";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";

import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";
import {
  featureButtonCss,
  reportButtonCss,
  rowCss,
  tooltipCss,
} from "./AIContentNotice.style";

type NoticeContentType = "ChecksForUnderstanding" | "Topic";

const noticeCopy = (type: NoticeContentType) => ({
  header:
    type === "ChecksForUnderstanding"
      ? "This content is Al-generated"
      : "These topics are Al-generated",
  body: "This content has been generated by an Al model, but the ideas and underlying gist are original and generated by a human author.",
});

const ButtonWithTrackEvent = withTrackEvent(Button);
const TooltipDialogWithTrackEvent = withTrackEvent(TooltipDialog);

const MODE = {
  ChecksForUnderstanding: "ChecksForUnderstanding",
  Topic: "Topic",
};

interface AINoticeProps {
  contentId: string;
  type: NoticeContentType;
  showLunaText?: boolean;
}

const AINotice = ({ contentId, type, showLunaText = false }: AINoticeProps) => {
  const [reportContentModalProps, setReportContentModalActive, triggerRef] =
    useModal(false);
  const { header, body } = noticeCopy(type);

  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  const { dispatch } = useAssignmentReducerContext() as {
    dispatch: () => void;
  };

  const streamProperties = {
    component_type: `AI${type}Notice`,
    content_id: contentId,
    content_type: `ArticleLevel ${type}`,
    object_id: contentId,
    object_type:
      type === MODE.ChecksForUnderstanding
        ? objectTypes.quiz
        : objectTypes.content,
  };

  const EVENT_TOAST_CHECKS_FOR_UNDERSTANDING_DISABLED = {
    showIcon: true,
    statusColor: Toast.statusColor.success,
    children:
      "Checks for Understanding disabled for this article. Turn them back on in your assignment settings.",
  };
  const handleHideChecksForUnderstandingClick = () => {
    handleHideChecksForUnderstanding(
      { hide_checks_for_understanding: true },
      dispatch,
    );
    globalToasterRef.current.displayToast(
      EVENT_TOAST_CHECKS_FOR_UNDERSTANDING_DISABLED,
    );
  };

  const DisableChecksForUnderstandingButton = () => {
    if (type === MODE.ChecksForUnderstanding) {
      return (
        <ButtonWithTrackEvent
          __cssFor={{ root: featureButtonCss }}
          dataProps={{
            "data-qa-selector": "disable_checks_for_understanding",
          }}
          flavor={Button.flavors.muted}
          size={Button.sizes.sm}
          icon={{
            SvgComponent: CogFilledSVG,
            alignment: Button.iconAlignments.left,
          }}
          onClick={() => {
            handleHideChecksForUnderstandingClick();
          }}
          objectType="GeneratedTextAsset"
          actionName="Hide"
          streamProperties={{
            content_type: "Article",
            description: "User clicked to disable Checks for Understanding",
            content_id: contentId,
          }}
        >
          Disable this feature
        </ButtonWithTrackEvent>
      );
    } else {
      return <></>;
    }
  };
  const ReportContentButton = () => {
    return (
      <ButtonWithTrackEvent
        actionName={actionNames.flag}
        flavor={Button.flavors.tertiary}
        size={Button.sizes.sm}
        __cssFor={{ root: reportButtonCss }}
        icon={{
          SvgComponent: FlagHollowSVG,
          alignment: Button.iconAlignments.left,
        }}
        onClick={() => setReportContentModalActive(true)}
        ref={triggerRef}
        objectType={streamProperties.object_type}
        streamProperties={{
          ...streamProperties,
          description: "Report Content",
        }}
        dataProps={{
          "data-qa-selector":
            type === MODE.ChecksForUnderstanding
              ? "article_checks_for_understanding_flag"
              : "article_topics_flag",
        }}
      >
        Report content
      </ButtonWithTrackEvent>
    );
  };

  const buttonContents = showLunaAiBranding ? (
    <AILunaTag
      enableHoverEffect
      textVersion={showLunaText ? "short" : "none"}
    />
  ) : (
    "AI"
  );

  return (
    <>
      <TooltipDialogWithTrackEvent
        actionName={actionNames.interact}
        actionPrefix={actionPrefixes.initiate}
        dataProps={{
          "data-qa-selector": "ai-info-button",
        }}
        objectType={objectTypes.quiz}
        buttonContents={buttonContents}
        placement="top-end"
        buttonProps={{
          flavor: Button.flavors.polarPrimary,
          size: Button.sizes.sm,
        }}
        __cssFor={tooltipCss(showLunaAiBranding, showLunaText)}
        body={body}
        footer={
          <div css={rowCss}>
            <DisableChecksForUnderstandingButton />
            <ReportContentButton />
          </div>
        }
        header={header}
      />
      <ReportContentFormModal
        contentId={contentId}
        reportContentModalProps={reportContentModalProps}
        setReportContentModalActive={setReportContentModalActive}
        reportSource={
          type === MODE.ChecksForUnderstanding
            ? "ChecksForUnderstanding"
            : "ArticleTopics"
        }
      />
    </>
  );
};

export default AINotice;
